import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { FaIndianRupeeSign } from "react-icons/fa6";
import { XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import InfoTooltip from "../../components/common/InfoTooltip";
const SAMPLE_TYPES = [
  "Blood",
  "Urine",
  "Stool",
  "Swab",
  "Tissue",
  "Saliva",
  "Skin Scraping",
  "N/A",
];

const INITIAL_FORM = {
  name: "",
  client_friendly_name: "",
  test_mode: "internal", // "internal" | "external"
  price: "",
  has_service_charge: false,
  tax_percentage: 0,
  status: "active",
  sample_type: "",
  category_id: "",
  description: "",
};

const LabTestDrawer = ({ isOpen, labTestId, categories = [], onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isOtherSampleType, setIsOtherSampleType] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (labTestId) {
        fetchLabTestDetails();
      } else {
        resetForm();
      }
    }
  }, [isOpen, labTestId]);

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setShowCategoryForm(false);
    setNewCategoryName("");
    setIsOtherSampleType(false);
  };

  const fetchLabTestDetails = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-lab-test/${labTestId}`
      );
      if (response.code === 200) {
        const data = response.data;
        const sampleType = data.sample_type || "";
        setIsOtherSampleType(sampleType !== "" && !SAMPLE_TYPES.includes(sampleType));
        setFormData({
          name: data.name || "",
          client_friendly_name: data.client_friendly_name || "",
          test_mode: data.test_mode || "internal",
          price: data.price ?? "",
          has_service_charge: data.has_service_charge || false,
          tax_percentage: data.tax_percentage || 0,
          status: data.status || "active",
          //sample_type: data.sample_type || "",
          sample_type: sampleType,
          category_id: data.category_id?._id || data.category_id || "",
          description: data.description || "",
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load lab test details");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    if (value === "") {
      setFormData((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleTestModeChange = (mode) => {
    setFormData((prev) => ({
      ...prev,
      test_mode: mode,
      // Reset price-dependent fields on every mode switch (either direction)
      // so a value entered under one mode never carries over stale into the other.
      price: "",
      has_service_charge: false,
      tax_percentage: 0,
    }));
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Category name is required");
      return;
    }
    try {
      const response = await httpService.post(
        `/vendorvetclinicservice/add-service-category`,
        { name: newCategoryName.trim() }
      );
      if (response.status) {
        toast.success("Category created successfully!");
        setShowCategoryForm(false);
        setNewCategoryName("");

        // Auto-select the newly created category
        const newCategoryId = response.data?._id;
        if (newCategoryId) {
          setFormData((prev) => ({ ...prev, category_id: newCategoryId }));
        }

        onSuccess?.();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create category");
    }
  };

  const validate = () => {
    if (!formData.name.trim()) {
      toast.error("Test name is required");
      return false;
    }
    if (!formData.sample_type.trim()) {
      toast.error("Sample type is required");
      return false;
    }
    if (formData.test_mode === "internal") {
      if (formData.price === "" || Number(formData.price) <= 0) {
        toast.error("Price is required for tests performed in-house");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const url = labTestId
        ? `/vendorvetclinicservice/update-lab-test/${labTestId}`
        : "/vendorvetclinicservice/add-lab-test";
      const method = labTestId ? "put" : "post";
      
      const { status, ...rest } = formData;
      const payload = {
        ...rest,
        price: formData.price === "" ? null : Number(formData.price),
        tax_percentage: formData.has_service_charge ? Number(formData.tax_percentage) || 0 : 0,
        ...(labTestId ? { status } : {}), // status only sent on update, not create
      };

      const response = await httpService[method](url, payload);
      if (response.status) {
        toast.success(
          labTestId ? "Lab test updated successfully!" : "Lab test created successfully!"
        );
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to save lab test");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isInternal = formData.test_mode === "internal";
  const priceEntered = formData.price !== "" && Number(formData.price) > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div
        className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
      />

      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="relative w-[750px] max-w-[98vw]">
          <div className="h-full flex flex-col bg-white shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  {labTestId ? "Edit Lab Test" : "Add New Lab Test"}
                </h2>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoaderSpiner text="Loading..." />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                  {/* 1. Name + 2. Client-Friendly Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Test Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="e.g., CBC (Complete Blood Count)"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Client-Friendly Name
                      </label>
                      <input
                        type="text"
                        name="client_friendly_name"
                        value={formData.client_friendly_name}
                        onChange={handleChange}
                        placeholder="e.g., Blood Test"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* 3. Test Mode */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Test Mode</label>
                      <InfoTooltip>
                        <p className="text-gray-300">
                          <span className="text-white font-medium">Internal:</span> you perform this test in-house and bill for it.
                        </p>
                        <p className="text-gray-300 mt-1">
                          <span className="text-white font-medium">External:</span> you refer the client elsewhere for this test.
                        </p>
                      </InfoTooltip>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="test_mode"
                          checked={isInternal}
                          onChange={() => handleTestModeChange("internal")}
                          className="h-4 w-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">Internal</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="test_mode"
                          checked={!isInternal}
                          onChange={() => handleTestModeChange("external")}
                          className="h-4 w-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">External</span>
                      </label>
                    </div>
                  </div>
                 {/* 3.a Price + 3.b Service Charge + 3.c Tax % — Internal only */}
                  {isInternal && (
                    <div className="flex items-end gap-6">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Price <span className="text-red-500">*</span>
                        </label>
                        <div className="relative mt-1">
                          <FaIndianRupeeSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="price"
                            value={formData.price}
                            onChange={handlePriceChange}
                            placeholder="Enter price"
                            className="w-full pl-9 py-2.5 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>

                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Add Separate Service Charges?
                        </label>
                        <div className="flex items-center gap-3 mt-1">
                          <label
                            className={`flex items-center gap-2 ${
                              priceEntered ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                            }`}
                          >
                            <input
                              type="radio"
                              name="has_service_charge"
                              disabled={!priceEntered}
                              checked={formData.has_service_charge === true}
                              onChange={() =>
                                setFormData((prev) => ({ ...prev, has_service_charge: true }))
                              }
                              className="h-4 w-4 text-blue-600"
                            />
                            <span className="text-sm text-gray-700">Yes</span>
                          </label>
                          <label
                            className={`flex items-center gap-2 ${
                              priceEntered ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                            }`}
                          >
                            <input
                              type="radio"
                              name="has_service_charge"
                              disabled={!priceEntered}
                              checked={formData.has_service_charge === false}
                              onChange={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  has_service_charge: false,
                                  tax_percentage: 0,
                                }))
                              }
                              className="h-4 w-4 text-blue-600"
                            />
                            <span className="text-sm text-gray-700">No</span>
                          </label>
                          {priceEntered && formData.has_service_charge && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-700">%</span>
                              <input
                                type="number"
                                name="tax_percentage"
                                value={formData.tax_percentage}
                                onChange={handlePriceChange}
                                placeholder="0"
                                className="w-20 border border-gray-300 rounded-md py-2 px-3 text-sm"
                              />
                            </div>
                          )}
                        </div>
                        {!priceEntered && (
                          <p className="text-xs text-gray-400 mt-1">Enter a price to enable this.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 5. Sample Type + 6. Category */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Sample Type
                      </label>
                      <select
                        name="sample_type"
                        value={
                          isOtherSampleType ? "Other" : formData.sample_type
                        }
                        onChange={(e) => {
                          if (e.target.value === "Other") {
                            setIsOtherSampleType(true);
                            setFormData((prev) => ({ ...prev, sample_type: "" }));
                          } else {
                            setIsOtherSampleType(false);
                            handleChange(e);
                          }
                        }}
                        className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Sample Type</option>
                        {SAMPLE_TYPES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                        <option value="Other">Other (specify)</option>
                      </select>
                      {isOtherSampleType && (
                        <input
                          type="text"
                          name="sample_type"
                          value={formData.sample_type}
                          onChange={handleChange}
                          placeholder="Enter sample type"
                          autoFocus
                          className="mt-2 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <div className="flex gap-2 mt-1">
                        <select
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleChange}
                          className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm"
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat._id} value={cat._id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowCategoryForm(!showCategoryForm)}
                          className="px-4 border border-blue-300 text-blue-600 rounded-md text-sm"
                        >
                          + New
                        </button>
                      </div>
                      {showCategoryForm && (
                        <div className="mt-2 flex gap-2">
                          <input
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="e.g., Hematology, Imaging"
                            className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm"
                          />
                          <button
                            type="button"
                            onClick={handleCreateCategory}
                            className="px-4 bg-blue-600 text-white rounded-md text-sm"
                          >
                            Create
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                 {/* 4. Status */}
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="status"
                          checked={formData.status === "active"}
                          onChange={() => setFormData((prev) => ({ ...prev, status: "active" }))}
                          className="h-4 w-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">Active</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="status"
                          checked={formData.status === "inactive"}
                          onChange={() => setFormData((prev) => ({ ...prev, status: "inactive" }))}
                          className="h-4 w-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">Inactive</span>
                      </label>
                    </div>
                  </div>

                  {/* 7. Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      placeholder="e.g., Checks red/white blood cell counts, detects infection or anemia"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  {!isInternal && (
                    <div className="flex gap-1 bg-blue-50 border border-blue-100 rounded-lg p-3">
                      <InformationCircleIcon className="h-5 w-5 text-blue-600 shrink-0" />
                      <p className="text-sm text-blue-800">
                        This test is referred externally. Price is optional — leave it blank if you don't charge or don't know the referral cost.
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="shrink-0 flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Saving..." : labTestId ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default LabTestDrawer;